import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {  FaChartLine, FaLaptopCode, FaLightbulb, FaShieldAlt, FaHandshake, FaUserShield, FaSearchDollar, FaFileAlt, FaCheckCircle } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";



const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

// Modification du ServiceCard pour ajouter plus d'espace en bas
const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px; /* On garde cette marge */
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#228B22"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const AdvantageCard = styled.div`
  padding: 25px 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const AdvantageIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#0d6efd"};
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const Audit = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const faqData = [
        {
            question: "Qu'est-ce qu'un audit comptable et financier ?",
            answer: "Un audit comptable et financier est un examen indépendant des états financiers d'une entreprise pour vérifier leur exactitude, leur fiabilité et leur conformité aux normes comptables en vigueur. Il vise à donner une assurance raisonnable que les comptes reflètent fidèlement la situation financière de l'entreprise."
        },
        {
            question: "Quelle est la différence entre un audit légal et un audit contractuel ?",
            answer: "L'audit légal est obligatoire pour certaines entreprises (par exemple, les sociétés cotées en bourse) et est réalisé par un commissaire aux comptes. L'audit contractuel est volontaire, demandé par l'entreprise elle-même ou par des tiers (banques, investisseurs), et peut être réalisé par un expert-comptable."
        },
        {
            question: "Pourquoi réaliser un audit ?",
            answer: "Un audit renforce la confiance des partenaires financiers, des investisseurs et des clients, détecte les anomalies et les risques financiers, améliore les processus internes et aide à la prise de décisions stratégiques."
        },
        {
            question: "Quelles entreprises ont besoin d'un audit légal ?",
            answer: "Dans l'espace OHADA, il s'agit des SA - sociétés anonymes (Art. 702 et s.) et des sociétés qui remplissent, à la clôture de l'exercice social, deux des conditions suivantes : (1) Total du bilan supérieur à cent vingt-cinq millions (125 000 000) de francs CFA ; (2) Chiffre d'affaires annuel supérieur à deux cent cinquante millions (250 000 000) de francs CFA ; (3) Effectif permanent supérieur à cinquante (50) personnes."
        },
        {
            question: "Quels types d'audit propose votre cabinet ?",
            answer: "Nous proposons des audits légaux, des audits contractuels, des audits d'acquisition, des audits de conformité, des audits internes et des audits spécifiques (par exemple, audit de systèmes d'information)."
        },
        {
            question: "Comment se déroule une mission d'audit ?",
            answer: "Une mission d'audit comprend généralement les étapes suivantes : planification, évaluation des risques, collecte et analyse des preuves, conclusions et rapport d'audit."
        },
        {
            question: "Quels sont les avantages de faire appel à CINEF-EC pour un audit ?",
            answer: "Notre cabinet vous apporte une expertise technique approfondie, une connaissance des normes comptables et d'audit, une approche rigoureuse et objective, et une communication claire et transparente."
        },
        {
            question: "Réalisez-vous des audits sectoriels spécialisés ?",
            answer: "Oui, nous avons une expertise dans l'audit de différents secteurs d'activité (industrie, services, associations, etc.) et nous adaptons nos méthodes aux spécificités de chaque secteur."
        },
        {
            question: "Comment garantissez-vous la confidentialité des informations ?",
            answer: "Nous sommes soumis au secret professionnel et nous mettons en place des mesures de sécurité rigoureuses pour protéger vos données."
        }
    ];

    const servicesData = [
        {
            icon: <FaFileAlt />,
            title: "Audit légal",
            description: "Certification de vos comptes annuels, dans le respect des obligations légales pour assurer la conformité de vos états financiers.",
            color: "#228B22"
        },
        {
            icon: <FaHandshake />,
            title: "Audit contractuel",
            description: "Audit de vos états financiers à la demande de vos partenaires (banques, investisseurs, etc.) pour renforcer leur confiance.",
            color: "#dc3545"
        },
        {
            icon: <FaSearchDollar />,
            title: "Audit d'acquisition",
            description: "Évaluation approfondie des risques financiers et comptables lors d'une opération d'acquisition ou de cession d'entreprise.",
            color: "#0d6efd"
        },
        {
            icon: <FaCheckCircle />,
            title: "Audit interne",
            description: "Évaluation de vos procédures de contrôle interne et recommandations personnalisées pour leur amélioration continue.",
            color: "#fd7e14"
        },
        {
            icon: <FaLaptopCode />,
            title: "Audit spécifique",
            description: "Audit de domaines particuliers (audit informatique, audit de conformité, etc.) adaptés à vos besoins spécifiques.",
            color: "#6f42c1"
        }
    ];

    const advantagesData = [
        {
            icon: <FaUserShield />,
            title: "Certification Experte",
            color: "#0d6efd"
        },
        {
            icon: <FaShieldAlt />,
            title: "Gestion des Risques",
            color: "#198754"
        },
        {
            icon: <FaHandshake />,
            title: "Confiance Partenaires",
            color: "#dc3545"
        },
        {
            icon: <FaLightbulb />,
            title: "Amélioration Continue",
            color: "#ffc107"
        }
    ];

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-8">
                            <h1 className="fw-bold display-4 mb-3">Services d'Audit</h1>
                            <p className="fs-5 mb-4">L'excellence et la rigueur au service de la transparence financière</p>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/" className="text-white text-decoration-none">
                                            Accueil
                                        </Link>{" "}
                                        /{" "}
                                        <Link to="/services" className="text-white text-decoration-none">
                                            Services
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-white" aria-current="page">
                                        Services d'Audit
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                L'audit de vos états financiers est un gage de confiance pour vos partenaires et
                                une garantie de la fiabilité de vos informations financières.
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                CINEF-EC vous accompagne dans la réalisation d'audits adaptés à vos besoins,
                                pour renforcer la transparence et la crédibilité de votre entreprise.
                            </p>
                        </div>
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="bg-light p-5 rounded-3">
                                <h3 className="fw-bold custom-title mb-4">
                                    Pourquoi faire appel à notre cabinet pour votre audit ?
                                </h3>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaShieldAlt className="fs-1 text-success" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Indépendance et objectivité</h5>
                                                <p className="text-muted">
                                                    Nous réalisons nos missions d'audit en toute indépendance,
                                                    pour vous garantir une évaluation objective de vos comptes.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaUserShield className="fs-1 text-primary" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Expertise reconnue</h5>
                                                <p className="text-muted">
                                                    Nos auditeurs sont des professionnels expérimentés,
                                                    maîtrisant les normes d'audit et les spécificités de votre secteur d'activité.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaChartLine className="fs-1 text-danger" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Vision globale</h5>
                                                <p className="text-muted">
                                                    Nous combinons notre expertise en audit avec notre connaissance
                                                    des enjeux comptables et fiscaux, pour vous offrir une vision complète de votre situation financière.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaHandshake className="fs-1 text-warning" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Accompagnement de qualité</h5>
                                                <p className="text-muted">
                                                    Nous vous accompagnons à chaque étape de la mission d'audit,
                                                    en vous tenant informés de nos constats et en vous apportant des recommandations constructives.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Nos services d'audit</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Des solutions adaptées à vos besoins spécifiques</p>
                        </div>
                    </div>

                    
                    <div className="row mb-4"> 
                        {servicesData.slice(0, 3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}> 
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>

                    <div className="row"> {/* Deuxième rangée pour les services restants */}
                        {servicesData.slice(3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index + 3}> {/* Ajout de mb-4 pour chaque carte */}
                                <ServiceCard delay={`${(index + 3) * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Renforcez la confiance de vos partenaires</h3>
                                <p className="text-white fs-5 mb-4">
                                    L'audit de vos comptes est un investissement dans la transparence et la crédibilité de votre entreprise.
                                    Contactez-nous dès aujourd'hui pour discuter de vos besoins en matière d'audit et découvrir comment nous pouvons vous accompagner.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un devis</a>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-12 text-center mb-5">
                            <h3 className="fw-bold">Les avantages de nos services</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        {advantagesData.map((item, index) => (
                            <div className="col-6 col-md-3" key={index}>
                                <AdvantageCard>
                                    <AdvantageIcon color={item.color}>{item.icon}</AdvantageIcon>
                                    <p className="fw-bold fs-5 mb-0">{item.title}</p>
                                </AdvantageCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa" id="faq-section" className={isVisible ? 'visible' : ''}>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h3 className="fw-bold">Questions Fréquentes sur l'Audit</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <FAQContainer>
                                <FAQContent>
                                    {faqData.map((faq, index) => (
                                        <FAQItem key={index} delay={`${index * 0.2}s`}>
                                            <Question onClick={() => toggleFAQ(index)}>
                                                {faq.question}
                                                <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                                            </Question>
                                            {activeIndex === index && <Answer>{faq.answer}</Answer>}
                                        </FAQItem>
                                    ))}
                                </FAQContent>
                            </FAQContainer>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/image-audit.png") no-repeat center center;
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
        </>
    );
};

export default Audit;